import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  TrendingUp, 
  Calendar, 
  LogOut,
  Target,
  BookOpen,
  Loader2,
  Home,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BreathingWidget } from '../components/BreathingWidget';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_DOMAIN = [0, 100];
const CHART_TOOLTIP_STYLE = { borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(15,23,42,0.06)', fontSize: '13px' };
const CHART_DOT = { fill: '#1e293b', strokeWidth: 0, r: 4 };
const CHART_ACTIVE_DOT = { r: 6, fill: '#0f172a' };

const Dashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/', { replace: true }); }
  }, [user, navigate]);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || 'U';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-white bg-black';
      case 'Very Good': return 'text-white bg-black';
      case 'Good': return 'text-amber-600 bg-amber-spark/10';
      default: return 'text-smoke bg-white/5';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const chartData = stats?.progress_history?.map((item, index) => ({
    name: `Test ${index + 1}`,
    score: item.score,
    date: new Date(item.date).toLocaleDateString()
  })) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-black/85 backdrop-blur-xl border-b border-white/10/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5" data-testid="dashboard-logo">
              <div className="w-9 h-9 rounded-xl bg-plum-voltage flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-lg text-white tracking-tight">Mindpuls<span className="underline decoration-slate-600 decoration-2 underline-offset-2">eI</span></span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/dashboard">
                <Button variant="ghost" className="rounded-full h-9 text-sm text-ash hover:text-white" data-testid="nav-dashboard">
                  <Home className="h-4 w-4 mr-1.5" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/emotions">
                <Button variant="ghost" className="rounded-full h-9 text-sm text-ash hover:text-white" data-testid="nav-emotions">
                  <BookOpen className="h-4 w-4 mr-1.5" />
                  Emotions
                </Button>
              </Link>
            </nav>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-black text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl">
              <div className="px-3 py-2">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="text-destructive cursor-pointer"
                data-testid="menu-logout"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-main">
        {/* Welcome */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl font-extrabold text-white tracking-tight mb-1">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-smoke text-sm">
            Track your emotional intelligence progress and continue your journey.
          </p>
        </div>

        {/* Bento Grid Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
          {/* Current EI Score - Large */}
          <Card className="rounded-2xl border-white/10 md:col-span-2 md:row-span-2 animate-slide-up" data-testid="ei-score-card" style={{opacity: 0}}>
            <CardContent className="p-8">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="text-sm text-smoke mb-1">Your EI Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-extrabold text-white tabular-nums">
                      {stats?.latest_result?.percentage?.toFixed(0) || '--'}
                    </span>
                    <span className="text-lg text-smoke">/100</span>
                  </div>
                </div>
                {stats?.latest_result?.level && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(stats.latest_result.level)}`}>
                    {stats.latest_result.level}
                  </span>
                )}
              </div>

              {stats?.latest_result?.category_scores && (
                <div className="space-y-4">
                  {Object.entries(stats.latest_result.category_scores).slice(0, 4).map(([category, score]) => (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="capitalize font-medium">{category.replace('_', ' ')}</span>
                        <span className="text-muted-foreground tabular-nums">{score}%</span>
                      </div>
                      <Progress value={score} className="h-2" />
                    </div>
                  ))}

                </div>
              )}

              {!stats?.latest_result && (
                <div className="text-center py-8">
                  <p className="text-smoke text-sm">No results yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tests Completed */}
          <Card className="rounded-2xl border-white/10 animate-slide-up delay-100" data-testid="tests-completed-card" style={{opacity: 0}}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
                  <Target className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-extrabold tabular-nums text-white">{stats?.total_assessments || 0}</p>
                  <p className="text-sm text-smoke">Tests Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Sessions */}
          <Card className="rounded-2xl border-white/10 animate-slide-up delay-200" data-testid="upcoming-sessions-card" style={{opacity: 0}}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-spark/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-extrabold tabular-nums text-white">{stats?.upcoming_bookings?.length || 0}</p>
                  <p className="text-sm text-smoke">Upcoming Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="rounded-2xl border-white/10 md:col-span-2 animate-slide-up delay-300" data-testid="quick-actions-card" style={{opacity: 0}}>
            <CardContent className="p-6">
              <p className="text-sm font-bold text-white mb-4">Quick Actions</p>
              <div className="grid grid-cols-2 gap-2.5">
                <Link to="/emotions">
                  <Button variant="outline" className="w-full justify-start rounded-xl h-11 text-sm border-white/10 text-ash hover:bg-black" data-testid="action-explore-emotions">
                    <BookOpen className="h-4 w-4 text-white mr-2 flex-shrink-0" />
                    Emotions
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Chart */}
        <Card className="rounded-2xl border-white/10 mb-8 animate-slide-up delay-400" data-testid="progress-chart-card" style={{opacity: 0}}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <TrendingUp className="h-4 w-4 text-white" />
              Progress Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                    <YAxis domain={CHART_DOMAIN} stroke="#94A3B8" fontSize={12} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#1e293b" 
                      strokeWidth={2.5}
                      dot={CHART_DOT}
                      activeDot={CHART_ACTIVE_DOT}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-56 flex items-center justify-center">
                <p className="text-smoke text-sm">Complete assessments to see your progress</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breathing Widget + Recommendations Row */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          <div className="md:col-span-1">
            <BreathingWidget />
          </div>
          <div className="md:col-span-2">
            {stats?.latest_result?.recommendations?.length > 0 ? (
              <Card className="rounded-2xl border-white/10 h-full" data-testid="recommendations-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-white">Personalized Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2.5">
                    {stats.latest_result.recommendations.map((rec, index) => (
                      <li key={rec} className="flex items-start gap-3 p-3 rounded-xl bg-black">
                        <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-white">{index + 1}</span>
                        </div>
                        <p className="text-ash text-sm leading-relaxed">{rec}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-2xl border-white/10 h-full flex items-center justify-center" data-testid="recommendations-empty">
                <CardContent className="text-center py-10">
                  <p className="text-smoke text-sm">Complete an assessment to get personalized recommendations</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Upcoming Bookings */}
        {stats?.upcoming_bookings?.length > 0 && (
          <Card className="rounded-2xl border-white/10 mb-6" data-testid="upcoming-bookings-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-white">
                <Calendar className="h-4 w-4 text-white" />
                Upcoming Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.upcoming_bookings.map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-black"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={booking.expert?.picture} />
                        <AvatarFallback className="text-sm">{booking.expert?.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{booking.expert?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.date} at {booking.time_slot}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-black text-white capitalize">
                      {booking.consultation_type}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
